import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { getBookingById, type Booking } from '../../services/booking.api';
import {
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle2,
  MapPin,
  Wrench,
  AlertCircle,
  Loader2,
  Package,
  FileText,
  Home,
  ChevronRight,
  Truck,
} from 'lucide-react';
import { Button } from '../../components/ui';
import { cn } from '../../lib/utils';
import { ROUTES, getOrderDetailRoute } from '../../config/routes';
import { TechnicianProfile, TimelineProgress, SmartBadge } from '../../components/orders-bookings';
import { bookingTitle } from '../../lib/bookingLabel';

const statusConfig = {
  pending: { 
    icon: Clock, 
    label: 'Pending', 
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400 dark:border-yellow-900' 
  },
  confirmed: { 
    icon: CheckCircle2, 
    label: 'Confirmed', 
    color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900' 
  },
  in_progress: { 
    icon: Wrench, 
    label: 'In Progress', 
    color: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900' 
  },
  completed: { 
    icon: CheckCircle2, 
    label: 'Completed', 
    color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900' 
  },
  cancelled: { 
    icon: AlertCircle, 
    label: 'Cancelled', 
    color: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900' 
  },
};

type Tab = 'service' | 'parts' | 'timeline';

/** Hero image for the booking — asset photo with an icon fallback. */
function BookingHeroImage({ src, alt, fallback }: { src: string | null; alt: string; fallback: React.ReactNode }) {
  const [failed, setFailed] = useState(false);
  const showImg = src && !failed;
  return (
    <div className="h-20 w-20 rounded-xl flex items-center justify-center border flex-shrink-0 overflow-hidden bg-muted">
      {showImg ? (
        <img src={src} alt={alt} className="h-full w-full object-contain p-1.5" onError={() => setFailed(true)} />
      ) : (
        fallback
      )}
    </div>
  );
}

export function BookingDetailPage() {
  const { t } = useTranslation('bookings');
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('service');

  useEffect(() => {
    if (bookingId) {
      loadBooking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getBookingById(bookingId!);
      setBooking(data);
    } catch (err: unknown) {
      console.error('Failed to load booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">{t('detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="flex items-center justify-center min-h-screen px-6 bg-background">
        <div className="text-center space-y-4 max-w-md">
          <div className="h-16 w-16 rounded-xl bg-red-50 dark:bg-red-950/20 flex items-center justify-center mx-auto border border-red-200 dark:border-red-900">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold">{t('detail.notFound')}</h2>
          <p className="text-sm text-muted-foreground">{error || t('detail.couldNotLoad')}</p>
          <Button onClick={() => navigate(ROUTES.BOOKINGS)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('detail.backToBookings')}
          </Button>
        </div>
      </div>
    );
  }

  const status = statusConfig[booking.status as keyof typeof statusConfig] || statusConfig.pending;
  const StatusIcon = status.icon;
  const scheduleDate = new Date(booking.scheduledDate);
  const isUpcoming = scheduleDate > new Date();

  const getServiceTimeline = () => {
    const steps = [
      { 
        label: t('detail.timelineStep.bookingCreated'), 
        completed: true, 
        date: new Date(booking.createdAt || booking.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        description: t('detail.timelineStep.submitted')
      },
      { 
        label: t('detail.timelineStep.confirmed_label'), 
        completed: booking.status !== 'pending',
        description: t('detail.timelineStep.assigned')
      },
      { 
        label: t('detail.timelineStep.scheduled'), 
        completed: booking.status === 'confirmed' || booking.status === 'in_progress' || booking.status === 'completed',
        date: scheduleDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        description: t('detail.timelineStep.confirmed')
      },
      { 
        label: t('detail.timelineStep.inProgress'), 
        completed: booking.status === 'in_progress' || booking.status === 'completed',
        description: t('detail.timelineStep.onSite')
      },
      { 
        label: t('detail.timelineStep.completed'), 
        completed: booking.status === 'completed',
        description: t('detail.timelineStep.finished')
      },
    ];

    return steps.map((step, index) => ({
      ...step,
      current: !step.completed && (index === 0 || steps[index - 1].completed),
    }));
  };

  const tabs: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }>; badge?: string }[] = [
    { id: 'service', label: t('detail.tabService'), icon: Wrench },
    { id: 'parts', label: t('detail.tabParts'), icon: Package, badge: booking.relatedOrders?.length ? String(booking.relatedOrders.length) : undefined },
    { id: 'timeline', label: t('detail.tabTimeline'), icon: Calendar },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-5xl px-6 lg:px-8 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.BOOKINGS)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('detail.backToBookings')}
          </Button>

          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <BookingHeroImage
                src={booking.asset?.imageUrl || null}
                alt={booking.asset?.name || booking.serviceType}
                fallback={<StatusIcon className="h-10 w-10 text-muted-foreground/70" />}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border",
                    status.color
                  )}>
                    <StatusIcon className="h-3.5 w-3.5" />
                    {status.label}
                  </span>
                  {isUpcoming && (booking.status === 'pending' || booking.status === 'confirmed') && (
                    <SmartBadge variant="today" size="small" />
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl font-light mb-2 truncate">
                  {bookingTitle(booking)}
                </h1>
                <div className="flex items-center gap-3 text-muted-foreground flex-wrap">
                  {booking.asset?.location && (
                    <span className="text-sm">{booking.asset.location}</span>
                  )}
                  {booking.home && (
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span className="text-sm">{booking.home.name}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="text-right flex-shrink-0">
              {booking.serviceFee && (
                <>
                  <div className="text-3xl font-semibold">
                    ${booking.serviceFee.toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{t('detail.serviceFeeLabel')}</div>
                </>
              )}
              <div className="mt-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-2 justify-end">
                  <Calendar className="h-3.5 w-3.5" />
                  {scheduleDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                <div className="flex items-center gap-2 justify-end mt-1">
                  <Clock className="h-3.5 w-3.5" />
                  {scheduleDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-2 border-b overflow-x-auto">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors relative",
                    isActive
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                  )}
                >
                  <TabIcon className="h-4 w-4" />
                  {tab.label}
                  {tab.badge && (
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="mx-auto max-w-5xl px-6 lg:px-8 py-8">
        {/* Service Details Tab */}
        {activeTab === 'service' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* Schedule Information */}
            <div className="rounded-xl border bg-card p-6">
              <h2 className="text-lg font-semibold mb-4">{t('detail.scheduleDetails')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-lg border bg-muted/30">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('detail.date')}</div>
                    <div className="font-medium">
                      {scheduleDate.toLocaleDateString('en-US', { 
                        weekday: 'long',
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 rounded-lg border bg-muted/30">
                  <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('detail.time')}</div>
                    <div className="font-medium">
                      {scheduleDate.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        timeZoneName: 'short'
                      })}
                    </div>
                  </div>
                </div>

                {booking.home && (
                  <div className="flex items-start gap-3 p-4 rounded-lg border bg-muted/30 md:col-span-2">
                    <Home className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <div className="text-xs text-muted-foreground mb-1">{t('detail.serviceLocation')}</div>
                      <div className="font-medium">{booking.home.name}</div>
                      {booking.home.address && (
                        <div className="text-sm text-muted-foreground mt-1">{booking.home.address}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Technician Profile - Mock data (would come from booking API) */}
            {(booking.status === 'confirmed' || booking.status === 'in_progress' || booking.status === 'completed') && (
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4 text-lg">{t('detail.yourTechnician')}</h3>
                <TechnicianProfile
                  variant="card"
                  name={booking.technicianName || "John Martinez"}
                  rating={4.8}
                  reviewCount={127}
                  phone="+1 (555) 123-4567"
                  email="john.m@homeiq.com"
                  specializations={['Plumbing', 'HVAC', 'Electrical']}
                  showContactButtons={true}
                  size="large"
                />
              </div>
            )}

            {/* Diagnosis Issue */}
            {booking.diagnosisIssue && (
              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-50 dark:bg-amber-950/20 flex items-center justify-center flex-shrink-0 border border-amber-200 dark:border-amber-900">
                    <Wrench className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('detail.diagnosedIssue')}</h3>
                    <p className="text-muted-foreground">{booking.diagnosisIssue}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Service Notes */}
            {booking.notes && (
              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('detail.serviceNotes')}</h3>
                    <p className="text-muted-foreground">{booking.notes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Parts Ordered Tab */}
        {activeTab === 'parts' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            {booking.relatedOrders && booking.relatedOrders.length > 0 ? (
              <>
                <div className="rounded-xl border bg-card p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-lg">{t('detail.partsForService')}</h3>
                    <span className="text-sm text-muted-foreground">
                      {booking.relatedOrders.length} {booking.relatedOrders.length === 1 ? t('detail.item') : t('detail.items')}
                    </span>
                  </div>

                  <div className="space-y-4">
                    {booking.relatedOrders.map((order) => (
                      <div
                        key={order.id}
                        onClick={() => navigate(getOrderDetailRoute(order.orderId))}
                        className="group p-5 rounded-lg border bg-muted/20 hover:bg-muted/40 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div className="flex items-start gap-4 flex-1 min-w-0">
                            <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 border">
                              <Package className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-lg mb-1 truncate group-hover:text-primary transition-colors">
                                {order.productName}
                              </h4>
                              <div className="text-sm text-muted-foreground mb-2 font-mono">
                                {t('detail.orderNumber', { id: order.orderId })}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={cn(
                                  "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border",
                                  order.status === 'delivered' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900' :
                                  order.status === 'shipped' ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900' :
                                  'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400 dark:border-yellow-900'
                                )}>
                                  {order.status && order.status.replace(/_/g, ' ')}
                                </span>
                                {order.status === 'shipped' && <SmartBadge variant="in_transit" size="small" />}
                                {order.status === 'delivered' && <SmartBadge variant="ready_to_install" size="small" />}
                              </div>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-2xl font-semibold">${order.totalAmount.toFixed(2)}</div>
                            {order.estimatedDelivery && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {t('detail.estPrefix', { date: new Date(order.estimatedDelivery).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) })}
                              </div>
                            )}
                          </div>
                        </div>

                        {order.trackingNumber && (
                          <div className="flex items-center gap-2 p-3 rounded-md bg-background border">
                            <Truck className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{t('detail.tracking')}</span>
                            <span className="text-sm font-mono font-medium">{order.trackingNumber}</span>
                          </div>
                        )}

                        <div className="mt-4 flex items-center gap-2 text-sm text-primary group-hover:text-primary/80">
                          <span>{t('viewFullOrderDetails')}</span>
                          <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {booking.relatedOrders.some((o: { status: string }) => o.status !== 'delivered' && o.status !== 'completed') && (
                  <div className="rounded-xl border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 p-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-900 dark:text-amber-200 mb-1">{t('detail.partsDeliveryStatus')}</h4>
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          {t('detail.partsDeliveryStatusBody')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-xl border bg-card p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-muted mb-4">
                  <Package className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-2">{t('detail.noPartsOrdered')}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {t('detail.noPartsOrderedDesc')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold mb-6 text-lg">{t('detail.serviceProgress')}</h3>
              <TimelineProgress steps={getServiceTimeline()} />
            </div>

            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold mb-4">{t('detail.keyDates')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-sm font-medium">{t('detail.bookingCreatedLabel')}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {new Date(booking.createdAt || booking.scheduledDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-2 w-2 rounded-full",
                      booking.status !== 'pending' ? 'bg-blue-500' : 'bg-gray-300'
                    )} />
                    <span className="text-sm font-medium">{t('detail.scheduledDateLabel')}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {scheduleDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                {booking.status === 'completed' && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      <span className="text-sm font-medium">{t('completed')}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {scheduleDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {isUpcoming && (
              <div className="rounded-xl border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900 p-6">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-1">{t('detail.upcomingAppointment')}</h4>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      {t('detail.upcomingAppointmentBody', {
                        date: scheduleDate.toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric'
                        }),
                        time: scheduleDate.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit'
                        })
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
